import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Upload, Download, Trash2, FileText, Eye, X, Star, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_TYPES = ['manual', 'purchase_document', 'warranty_document', 'insurance_policy', 'picture'];

const downloadFile = async (assetId, gridfsId, filename) => {
  try {
    const r = await axios.get(`${API}/inventory/assets/${assetId}/files/${gridfsId}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.click();
    window.URL.revokeObjectURL(url);
  } catch { toast.error(i18n.t('auto.inv.downloadFailed')); }
};

const AssetFiles = ({ asset, isAdmin, onRefresh }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [fileType, setFileType] = useState('picture');
  const fileRef = useRef(null);
  const [picUrls, setPicUrls] = useState({});
  const loadedRef = React.useRef(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [reordering, setReordering] = useState(false);

  React.useEffect(() => {
    const pictures = asset.pictures || [];
    pictures.forEach(async (gridfsId) => {
      if (loadedRef.current.has(gridfsId)) return;
      loadedRef.current.add(gridfsId);
      try {
        const r = await axios.get(`${API}/inventory/assets/${asset.id}/files/${gridfsId}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(r.data);
        setPicUrls((prev) => ({ ...prev, [gridfsId]: url }));
      } catch {
        loadedRef.current.delete(gridfsId);
      }
    });
  }, [asset.id, asset.pictures]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(t('inv.files.tooLarge')); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/inventory/assets/${asset.id}/upload/${fileType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('inv.files.uploaded'));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.uploadFailed')); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeletePicture = async (gridfsId) => {
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/pictures/${gridfsId}`);
      toast.success(t('inv.files.pictureDeleted'));
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.deleteFailed')); }
  };

  const openPreview = (gridfsId) => {
    setPreviewId(gridfsId);
    setPreviewUrl(picUrls[gridfsId] || null);
    setPreviewOpen(true);
  };

  // The first entry in `pictures` is the lead photo: it is what the public
  // QR page and the asset PDF show first, so staff need to control it.
  const saveOrder = async (next) => {
    setReordering(true);
    try {
      await axios.put(`${API}/inventory/assets/${asset.id}/pictures/order`, { order: next });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
    setReordering(false);
  };

  const movePicture = (e, gridfsId, delta) => {
    e.stopPropagation();
    const next = [...(asset.pictures || [])];
    const i = next.indexOf(gridfsId);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    saveOrder(next);
  };

  const makeLead = (e, gridfsId) => {
    e.stopPropagation();
    const rest = (asset.pictures || []).filter((p) => p !== gridfsId);
    saveOrder([gridfsId, ...rest]);
  };

  const openDeleteConfirm = (e, gridfsId) => {
    e.stopPropagation();
    setDeleteTargetId(gridfsId);
    setDeleteConfirmOpen(true);
  };

  const docFiles = [
    { key: 'manual', file: asset.manual_file, gridfs: asset.manual_file_gridfs_id },
    { key: 'purchase_document', file: asset.purchase_document_file, gridfs: asset.purchase_document_gridfs_id },
    { key: 'warranty_document', file: asset.warranty_document_file, gridfs: asset.warranty_document_gridfs_id },
    { key: 'insurance_policy', file: asset.insurance?.insurance_policy_file, gridfs: asset.insurance?.insurance_policy_gridfs_id },
  ].filter((d) => d.file || d.gridfs);

  const pictures = asset.pictures || [];

  return (
    <div className="space-y-4" data-testid="asset-files">
      {isAdmin && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-end gap-3">
              <div>
                <Select value={fileType} onValueChange={setFileType}>
                  <SelectTrigger className="w-[180px]" data-testid="file-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FILE_TYPES.map((v) => (<SelectItem key={v} value={v}>{t(`inv.files.types.${v}`)}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <input type="file" ref={fileRef} onChange={handleUpload} className="hidden" accept={fileType === 'picture' ? 'image/*' : '*'} />
                <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading || (fileType === 'picture' && pictures.length >= 5)} data-testid="upload-file-btn">
                  <Upload className="w-4 h-4 mr-1" /> {uploading ? t('inv.files.uploading') : t('inv.files.upload')}
                </Button>
              </div>
              {fileType === 'picture' && <span className="text-xs text-muted-foreground">{t('inv.files.pictureCount', { count: pictures.length })}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {docFiles.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2">{t('inv.files.documents')}</h4>
          <div className="space-y-2">
            {docFiles.map((d) => (
              <div key={d.key} className="flex items-center justify-between py-2 px-3 border rounded-md" data-testid={`doc-file-${d.key}`}>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{t(`inv.files.types.${d.key}`)}</span>
                  <Badge variant="secondary" className="text-xs">{d.file}</Badge>
                </div>
                {d.gridfs && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadFile(asset.id, d.gridfs, d.file)} data-testid={`download-${d.key}`}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {pictures.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2">{t('inv.files.pictures', { count: pictures.length })}</h4>
          {isAdmin && pictures.length > 1 && (
            <p className="text-xs text-muted-foreground mb-2" data-testid="lead-photo-hint">{t('inv.files.leadHint')}</p>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {pictures.map((gridfsId, idx) => (
              <div key={gridfsId} className="relative group rounded-md overflow-hidden border" data-testid={`picture-${gridfsId}`}>
                {picUrls[gridfsId] ? (
                  <img
                    src={picUrls[gridfsId]}
                    alt={t('auto.inv.asset')}
                    className="w-full h-24 object-cover cursor-pointer"
                    onClick={() => openPreview(gridfsId)}
                    data-testid={`picture-preview-${gridfsId}`}
                  />
                ) : (
                  <div className="w-full h-24 bg-muted flex items-center justify-center text-xs text-muted-foreground">{t('common.loading')}</div>
                )}
                {idx === 0 && (
                  <Badge className="absolute top-1 left-1 gap-1 px-1.5 py-0 text-[10px] bg-amber-500 hover:bg-amber-500" data-testid={`lead-photo-badge-${gridfsId}`}>
                    <Star className="w-2.5 h-2.5 fill-current" /> {t('inv.files.lead')}
                  </Badge>
                )}
                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 p-1 bg-black/40 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  {isAdmin && pictures.length > 1 && (
                    <>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20 disabled:opacity-30"
                        disabled={reordering || idx === 0} onClick={(e) => movePicture(e, gridfsId, -1)}
                        title={t('inv.files.moveEarlier')} data-testid={`move-picture-left-${gridfsId}`}>
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20 disabled:opacity-30"
                        disabled={reordering || idx === pictures.length - 1} onClick={(e) => movePicture(e, gridfsId, 1)}
                        title={t('inv.files.moveLater')} data-testid={`move-picture-right-${gridfsId}`}>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-amber-300 hover:bg-white/20 disabled:opacity-30"
                        disabled={reordering || idx === 0} onClick={(e) => makeLead(e, gridfsId)}
                        title={t('inv.files.setLead')} data-testid={`set-lead-picture-${gridfsId}`}>
                        <Star className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={() => openPreview(gridfsId)} data-testid={`view-picture-${gridfsId}`}>
                    <Eye className="w-3 h-3" />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-300 hover:bg-red-500/30" onClick={(e) => openDeleteConfirm(e, gridfsId)} data-testid={`delete-picture-${gridfsId}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {docFiles.length === 0 && pictures.length === 0 && (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('inv.files.empty')}</p>
      )}

      {/* Full-Size Image Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="image-preview-dialog">
          <DialogHeader>
            <DialogTitle>{t('inv.files.imagePreview')}</DialogTitle>
            <DialogDescription className="sr-only">{t('inv.files.imagePreview')}</DialogDescription>
          </DialogHeader>
          {previewUrl && (
            <div className="flex items-center justify-center max-h-[70vh]">
              <img src={previewUrl} alt={t('auto.inv.fullPreview')} className="max-h-[65vh] max-w-full object-contain rounded" />
            </div>
          )}
          <DialogFooter className="flex justify-between">
            {isAdmin && previewId && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => { setPreviewOpen(false); setDeleteTargetId(previewId); setDeleteConfirmOpen(true); }}
                data-testid="preview-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> {t('common.delete')}
              </Button>
            )}
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="dialog-close-btn">{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('inv.files.confirmDeletion')}</DialogTitle>
            <DialogDescription>{t('inv.files.confirmDeleteHint')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => handleDeletePicture(deleteTargetId)} data-testid="confirm-delete-btn">
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetFiles;
